import { ArrowUpRightIcon, CaretRightIcon, WarningCircleIcon } from "@phosphor-icons/react"
import { motion } from "motion/react"
import type { ReactNode } from "react"

import { IconButton } from "~/components/design-system/components"
import InspectorDrilldown from "~/components/design-system/pages/InspectorDrilldown"
import { type InspectorWidth } from "~/components/design-system/pages/types"
import { Tooltip } from "~/components/reusable/Tooltip"
import {
  ExpandableCard,
  ExpandableCardContent,
  useExpandableCardToggle,
} from "~/components/design-system/expandable-card"
import { cn } from "~/utils/helpers"

type DrilldownConfig = {
  /** Headline the inspector nav shows while the drilldown is open. Defaults to the card title. */
  title?: string
  /** Px floor while open, only for content that can't work at inspector width. */
  minWidth?: number
  /** Accessible name of the `↗` trigger. Defaults to `Open <title> detail`. */
  label?: string
  children: ReactNode
  /** @deprecated No longer used: a drilldown follows the inspector's width. Pass `minWidth` instead. */
  width?: InspectorWidth
}

/** A card offered in name only: header stays, content never renders, and the right slot says why. */
export type InspectorCardDisabled = boolean | { label?: string }

// Says why the card is shut and what opens it, where a bare "Disabled" reads as a fault. The
// inspector's one disabled state today is a multi-variant selection.
const DISABLED_LABEL = "Single variant only"

/**
 * `InspectorCard`'s header, for the rare card that must wrap both slots in a provider (see
 * `AdjustmentsCard`) and so cannot use the all-in-one form. Pair with `InspectorCardContent`
 * inside your own `<ExpandableCard>`.
 */
export const InspectorCardHeader = ({
  title,
  badges,
  action,
  drilldown,
  disabled,
  alert,
}: {
  title: string
  badges?: ReactNode
  action?: ReactNode
  drilldown?: DrilldownConfig
  disabled?: InspectorCardDisabled
  alert?: string | null
}) => {
  // The interactive-target guard lives in the hook; re-implementing it breaks click-to-collapse.
  // Called unconditionally (hook rule); a disabled header just doesn't spread its props.
  const { isExpanded, toggleProps } = useExpandableCardToggle()
  const isDisabled = disabled === true || (typeof disabled === "object" && disabled != null)
  const disabledLabel =
    (typeof disabled === "object" && disabled != null && disabled.label) || DISABLED_LABEL

  return (
    <div
      {...(isDisabled ? { "aria-disabled": true } : toggleProps)}
      className={cn(
        // Transparent: ExpandableCard paints the hover behind it.
        "flex items-center gap-2 p-3 select-none",
        !isDisabled && "cursor-pointer focus-visible:outline-none",
      )}
    >
      {/* Badges wrap rather than squeezing the title. Title and caret are one non-wrapping group:
          a bare `truncate` flex item claims its full width and pushes the caret to a new line. */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="flex min-w-0 max-w-full items-center gap-2">
          <motion.span
            animate={{ rotate: isExpanded && !isDisabled ? 90 : 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 30, mass: 0.6 }}
            className="flex shrink-0"
          >
            <CaretRightIcon
              size={16}
              className={isDisabled ? "text-fg-disabled" : "text-fg-muted"}
            />
          </motion.span>
          <span className="label-base text-fg-base truncate">{title}</span>
          {alert && (
            <Tooltip title={alert}>
              <WarningCircleIcon
                size={16}
                weight="fill"
                className="shrink-0 text-fg-error-icon"
                aria-label={alert}
              />
            </Tooltip>
          )}
        </span>
        {badges}
      </div>
      {isDisabled ? (
        <span className="label-sm text-fg-disabled shrink-0">{disabledLabel}</span>
      ) : (
        (action || drilldown) && (
          <div className="flex shrink-0 items-center gap-1">
            {action}
            {drilldown && (
              <InspectorDrilldown
                title={drilldown.title ?? title}
                minWidth={drilldown.minWidth}
                trigger={({ open }) => (
                  <IconButton
                    size="xs"
                    variant="transparent"
                    aria-label={drilldown.label ?? `Open ${title} detail`}
                    onClick={open}
                  >
                    <ArrowUpRightIcon size={16} />
                  </IconButton>
                )}
              >
                {drilldown.children}
              </InspectorDrilldown>
            )}
          </div>
        )
      )}
    </div>
  )
}

/** Companion to {@link InspectorCardHeader} — the card's content slot with the inspector's padding. */
export const InspectorCardContent = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => (
  <ExpandableCardContent className={cn("px-3 pt-3 pb-3", className)}>
    {children}
  </ExpandableCardContent>
)

/**
 * The inspector's card. One per topic (Details, Availability, Performance…), stacked in the inspector
 * column; collapsed it is header-only.
 *
 * Header reads `Title ⌄  [badges] … ↗`. `↗` is the *uniform* "deeper detail" affordance: pass
 * `drilldown` and the card wires {@link InspectorDrilldown} rather than each card inventing an
 * icon. `action` is for anything that isn't a drilldown. `disabled` keeps the card as a collapsed
 * shell with a "Single variant only" label, for a step that exists but is not offered in this state.
 *
 * Collapse, `persistKey` persistence and animation come from `ExpandableCard`; this only replaces
 * its header layout. Use `useInspectorExpandKey` for `persistKey`. Inside a card, use
 * `InformationGrid` + `InformationRow` for properties, `Kpi` for a figure.
 *
 * Canonical intent: {@link ../intents.ts INSPECTOR_CARD_INTENT}. Approved surface: APPROVED.md.
 *
 * @example
 * <InspectorCard
 *   title="Baseline"
 *   badges={<Badge variant="gray" size="xs">Last 52w</Badge>}
 *   persistKey={expandKey("baseline")}
 *   drilldown={{ children: <BaselineDrilldown … /> }}
 * >
 *   <InformationGrid>
 *     <InformationRow title="Source" value="Sales" />
 *   </InformationGrid>
 * </InspectorCard>
 */
const InspectorCard = ({
  title,
  badges,
  action,
  drilldown,
  persistKey,
  defaultExpanded,
  expanded,
  onExpandedChange,
  className,
  contentClassName,
  disabled,
  alert,
  children,
}: {
  title: string
  badges?: ReactNode
  /** Far-right control that is NOT a drilldown; renders before `↗`. */
  action?: ReactNode
  drilldown?: DrilldownConfig
  persistKey?: string
  defaultExpanded?: boolean
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  className?: string
  contentClassName?: string
  /** Header-only shell with a trailing "Single variant only" label; the content never mounts. */
  disabled?: InspectorCardDisabled
  alert?: string | null
  children?: ReactNode
}) => (
  <ExpandableCard
    persistKey={persistKey}
    defaultExpanded={defaultExpanded}
    // Forced shut, which also stops `persistKey` recording a state the planner never chose.
    expanded={disabled ? false : expanded}
    onExpandedChange={onExpandedChange}
    className={className}
  >
    <InspectorCardHeader
      title={title}
      badges={badges}
      action={action}
      drilldown={drilldown}
      disabled={disabled}
      alert={alert}
    />
    <InspectorCardContent className={contentClassName}>{children}</InspectorCardContent>
  </ExpandableCard>
)

export default InspectorCard
